import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import Logo from '../../common/Logo';
import DimensionsSection from './DimensionsSection';
import MetricsSection from './MetricsSection';
import FiltersSection from './FiltersSection';
import PreviewTable from './PreviewTable';
import DataCaveatNote from './DataCaveatNote';
import RecentExports from './RecentExports';
import { previewQueryBuilder, runQueryBuilderExport } from '../../../api/admin';
import { Dimension, Metric, QueryBuilderColumn, QueryBuilderPreviewResponse } from './types';

// ---- Types ----

interface DateRange {
  startDate: string;
  endDate: string;
}

interface FiltersState {
  dateRange: DateRange;
  campus: string[];
  grade: number[];
  track: string[];
}

type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; columns: QueryBuilderColumn[]; rows: Array<Record<string, string | number | null>>; rowCount: number; appliedRules: QueryBuilderPreviewResponse['appliedRules']; truncated: boolean }
  | { status: 'error'; message: string };

type ExportState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; message: string }
  | { status: 'error'; message: string };

interface DateErrors {
  startDate?: string;
  endDate?: string;
}

// ---- Helpers ----

function getDefaultDates(): { startDate: string; endDate: string } {
  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);
  return {
    startDate: sevenDaysAgo.toISOString().split('T')[0],
    endDate: today.toISOString().split('T')[0],
  };
}

function validateDates(startDate: string, endDate: string): DateErrors {
  const errors: DateErrors = {};
  if (!startDate) {
    errors.startDate = 'Start date is required.';
  }
  if (!endDate) {
    errors.endDate = 'End date is required.';
  }
  if (startDate && endDate && startDate > endDate) {
    errors.startDate = 'Start date must be on or before end date.';
  }
  return errors;
}

// ---- Subcomponents ----

const AccessDenied = () => (
  <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center p-6">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
      <p className="text-base text-gray-600 mt-2">You are not authorized to view this page.</p>
    </div>
  </div>
);

const PageHeader = () => (
  <h1 className="text-4xl font-bold text-center mb-6 flex items-center justify-center gap-4">
    <Logo size={32} />
    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
      Query Builder
    </span>
  </h1>
);

// ---- Main Component ----

export const QueryBuilderPage: React.FC = () => {
  const { user: currentUser } = useAuth();

  const isAdmin = useMemo(
    () => currentUser?.role === 'admin',
    [currentUser?.role]
  );

  const defaultDates = useMemo(() => getDefaultDates(), []);

  // Form state
  const [selectedDimensions, setSelectedDimensions] = useState<Dimension[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<Metric[]>([]);
  const [filters, setFilters] = useState<FiltersState>({
    dateRange: defaultDates,
    campus: [],
    grade: [],
    track: [],
  });

  // Request/response state
  const [preview, setPreview] = useState<PreviewState>({ status: 'idle' });
  const [exportStatus, setExportStatus] = useState<ExportState>({ status: 'idle' });
  const [hasRunPreviewSuccessfully, setHasRunPreviewSuccessfully] = useState(false);

  // Incrementing this triggers RecentExports to refetch immediately
  const [exportsRefetchTrigger, setExportsRefetchTrigger] = useState(0);

  // Date validation errors (shown on submit attempt)
  const [dateErrors, setDateErrors] = useState<DateErrors>({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Reset preview + export gates when any form field changes
  const resetPreviewGate = () => {
    setHasRunPreviewSuccessfully(false);
    setExportStatus({ status: 'idle' });
  };

  const handleDimensionsChange = (dimensions: Dimension[]) => {
    setSelectedDimensions(dimensions);
    resetPreviewGate();
  };

  const handleMetricsChange = (metrics: Metric[]) => {
    setSelectedMetrics(metrics);
    resetPreviewGate();
  };

  const handleFiltersChange = (newFilters: FiltersState) => {
    setFilters(newFilters);
    resetPreviewGate();
  };

  // Derived validation
  const dateValidationErrors = useMemo(
    () => validateDates(filters.dateRange.startDate, filters.dateRange.endDate),
    [filters.dateRange.startDate, filters.dateRange.endDate]
  );

  const isFormValid =
    selectedDimensions.length >= 1 &&
    selectedMetrics.length >= 1 &&
    Object.keys(dateValidationErrors).length === 0 &&
    filters.dateRange.startDate !== '' &&
    filters.dateRange.endDate !== '';

  const isPreviewEnabled = isFormValid;
  const isExportEnabled = isFormValid && hasRunPreviewSuccessfully;

  const buildRequest = () => ({
    dimensions: selectedDimensions,
    metrics: selectedMetrics,
    filters: {
      dateRange: filters.dateRange,
      ...(filters.campus.length > 0 ? { campus: filters.campus } : {}),
      ...(filters.grade.length > 0 ? { grade: filters.grade } : {}),
      ...(filters.track.length > 0 ? { track: filters.track } : {}),
    },
  });

  const handlePreview = async () => {
    if (!isPreviewEnabled) {
      setShowValidationErrors(true);
      setDateErrors(dateValidationErrors);
      return;
    }
    setShowValidationErrors(false);
    setDateErrors({});
    setPreview({ status: 'loading' });
    setHasRunPreviewSuccessfully(false);

    try {
      const response = await previewQueryBuilder(buildRequest());
      setPreview({
        status: 'success',
        columns: response.columns,
        rows: response.rows,
        rowCount: response.rowCount,
        appliedRules: response.appliedRules,
        truncated: response.truncated,
      });
      setHasRunPreviewSuccessfully(true);
    } catch {
      setPreview({ status: 'error', message: 'Preview failed. Please try again.' });
    }
  };

  const handleExport = async () => {
    if (!isExportEnabled) return;
    setExportStatus({ status: 'loading' });

    try {
      const response = await runQueryBuilderExport(buildRequest());
      setExportStatus({ status: 'success', message: response.message });
      // Refetch the recent exports list so the new QUEUED row appears immediately
      setExportsRefetchTrigger((n) => n + 1);
    } catch {
      setExportStatus({ status: 'error', message: 'Could not start the export. Please try again.' });
    }
  };

  // Show date errors if user has attempted submit
  useEffect(() => {
    if (showValidationErrors) {
      setDateErrors(dateValidationErrors);
    }
  }, [dateValidationErrors, showValidationErrors]);

  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6">
      <div className="max-w-6xl mx-auto space-y-8">
        <PageHeader />

        {/* Back nav */}
        <div className="flex mb-4">
          <Link
            to="/admin"
            className="text-blue-600 hover:text-blue-800 transition-colors duration-300"
          >
            &larr; Back to Admin Dashboard
          </Link>
        </div>

        <Card className="overflow-hidden rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-4 px-6">
            <h2 className="text-2xl font-semibold text-white">
              Build Custom Report
            </h2>
            <p className="text-blue-100 text-sm mt-1">
              Select dimensions, metrics, and filters to build a custom CSV report.
              Preview 10 rows first, then run the full export — results will be emailed to you.
            </p>
          </div>

          <CardContent className="p-6 space-y-6">
            {/* Dimensions */}
            <DimensionsSection
              selectedDimensions={selectedDimensions}
              onChange={handleDimensionsChange}
            />

            {/* Metrics */}
            <MetricsSection
              selectedMetrics={selectedMetrics}
              onChange={handleMetricsChange}
            />

            {/* Filters */}
            <FiltersSection
              filters={filters}
              onChange={handleFiltersChange}
              dateErrors={dateErrors}
            />

            {/* Data Caveat Banner */}
            <DataCaveatNote
              startDate={filters.dateRange.startDate}
              endDate={filters.dateRange.endDate}
            />

            {/* Validation summary */}
            {showValidationErrors && !isFormValid && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 space-y-1">
                {selectedDimensions.length === 0 && <p>At least one dimension must be selected.</p>}
                {selectedMetrics.length === 0 && <p>At least one metric must be selected.</p>}
                {dateErrors.startDate && <p>{dateErrors.startDate}</p>}
                {dateErrors.endDate && <p>{dateErrors.endDate}</p>}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3 items-center">
              <Button
                onClick={handlePreview}
                disabled={!isPreviewEnabled || preview.status === 'loading'}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-bold text-base shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {preview.status === 'loading' ? 'Loading preview...' : 'Preview'}
              </Button>

              <Button
                onClick={handleExport}
                disabled={!isExportEnabled || exportStatus.status === 'loading'}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-bold text-base shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportStatus.status === 'loading' ? 'Starting export...' : 'Run Full Export'}
              </Button>

              {!hasRunPreviewSuccessfully && exportStatus.status === 'idle' && (
                <p className="text-xs text-gray-400">
                  Run a successful preview first to enable export.
                </p>
              )}
            </div>

            {/* Export status messages */}
            {exportStatus.status === 'success' && (
              <div className="p-4 bg-green-50 border border-green-300 rounded-lg text-sm text-green-800">
                {exportStatus.message}
              </div>
            )}
            {exportStatus.status === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {exportStatus.message}
              </div>
            )}

            {/* Preview area */}
            {preview.status !== 'idle' && (
              <div className="mt-4">
                <h3 className="text-lg font-medium text-gray-800 mb-3">Preview Results</h3>
                {preview.status === 'loading' && (
                  <div className="p-6 text-center text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                    Loading preview...
                  </div>
                )}
                {preview.status === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {preview.message}
                  </div>
                )}
                {preview.status === 'success' && (
                  <PreviewTable
                    columns={preview.columns}
                    rows={preview.rows}
                    rowCount={preview.rowCount}
                    truncated={preview.truncated}
                  />
                )}
              </div>
            )}

            {/* Recent exports */}
            <RecentExports refetchTrigger={exportsRefetchTrigger} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QueryBuilderPage;
